import { HttpClient, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { CSRF_ENDPOINT } from '../../shared/constants/endpoints';
import { getCookie, hasCookie } from '../../shared/utils/cookie';
import { CsrfData } from '../../shared/dtos/auth';
import { ApiResponse } from '../../shared/dtos/api-response';

/** Cookie name the backend sets (e.g. Spring CookieCsrfTokenRepository default). */
const CSRF_COOKIE_NAME = 'XSRF-TOKEN';

/** Header name the backend expects (e.g. X-XSRF-TOKEN). */
const CSRF_HEADER_NAME = 'X-XSRF-TOKEN';

const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Shared fetch so concurrent state-changing requests without a cookie trigger only one CSRF GET. */
let csrfFetch$: Observable<string | null> | null = null;

/**
 * Adds the CSRF token from the cookie to state-changing requests.
 * The server sets the token in a cookie (e.g. XSRF-TOKEN); we send it back in the header.
 * If the CSRF cookie is missing (e.g. after logout or manual deletion), fetches a new one
 * from the server and then proceeds with the request.
 */
export const csrfInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  // Non state-changing methods don't need a CSRF token
  if (!STATE_CHANGING_METHODS.has(req.method)) {
    return next(req);
  }

  // Ensure CSRF cookie exists before state-changing requests (logout or manual delete can remove it)
  if (!hasCookie(CSRF_COOKIE_NAME)) {
    // Create a single observable to fetch CSRF token
    const http = inject(HttpClient);
    csrfFetch$ ??= http.get<ApiResponse<CsrfData>>(CSRF_ENDPOINT, { withCredentials: true }).pipe(
      take(1),
      map((resp) => resp.data.token),
      catchError(() => of(null)),
      shareReplay(1),
    );

    // Return an observable waiting the token
    return csrfFetch$.pipe(
      switchMap((token) => next(cloneReqWithTokenHeader(req, token))),
      finalize(() => {
        csrfFetch$ = null;
      }), // Reset global observable
    );
  }

  // Get the CSRF token from the document cookies
  const token = getCookie(CSRF_COOKIE_NAME);
  if (!token) {
    return next(req);
  }

  // Add the CSRF token to the request
  return next(cloneReqWithTokenHeader(req, token));
};

// Helper method to clone request
const cloneReqWithTokenHeader = (
  req: HttpRequest<unknown>,
  token: string | null,
): HttpRequest<unknown> => {
  const cloned = req.clone({
    setHeaders: { [CSRF_HEADER_NAME]: token || '' },
  });
  return cloned;
};
